/**
 * Web Push client — Phase 7.
 *
 * Talks to two layers:
 *   1. The browser's `pushManager` (subscribe / unsubscribe / permission).
 *   2. Our backend REST endpoints:
 *        GET  /api/push/vapid-public-key   (public — no auth cookie needed)
 *        POST /api/push/subscribe          (auth required)
 *        POST /api/push/unsubscribe        (auth required)
 *        GET  /api/push/status             (auth required)
 *
 * All errors are surfaced to the caller — the caller (TopBar toggle /
 * onboarding banner) decides how to render them. No global side-effects
 * beyond installing a `message` listener on the Service Worker for the
 * "navigate to conversation" postMessage from the SW.
 */

import { api } from "@/lib/api";

// ---------- Base capability check ----------

export const pushSupported = () =>
    typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;

// ---------- Service Worker registration (idempotent) ----------

let _swReg = null;
let _swRegPromise = null;

/**
 * Ensure our service worker is registered. Idempotent — repeated calls
 * return the same ServiceWorkerRegistration. Safe to call from React.
 */
export const ensureServiceWorker = () => {
    if (_swRegPromise) return _swRegPromise;
    if (!pushSupported()) return Promise.resolve(null);
    _swRegPromise = navigator.serviceWorker
        .register("/service-worker.js", { scope: "/" })
        .then(async (reg) => {
            _swReg = reg;
            // Kick the worker so it's active on first visit — otherwise
            // subscribe() below will fail until the user does a hard reload.
            await navigator.serviceWorker.ready;
            return reg;
        })
        .catch((e) => {
            console.warn("[push] service worker registration failed:", e);
            _swRegPromise = null;   // allow retry
            return null;
        });
    return _swRegPromise;
};

// ---------- VAPID key helpers ----------

const urlBase64ToUint8Array = (base64String) => {
    // Padding + URL-safe → standard base64 → binary.
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const b64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(b64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
};

let _cachedVapid = null;
const fetchVapidKey = async () => {
    if (_cachedVapid) return _cachedVapid;
    const { data } = await api.get("/push/vapid-public-key");
    _cachedVapid = data.public_key;
    return _cachedVapid;
};

// ---------- Status / subscription ----------

/**
 * Snapshot of the current push state for UI gating. Cheap by default; when
 * `withBackendStatus=true` also fetches `GET /api/push/status` so the caller
 * can detect the **desync case** (browser has a Subscription object but the
 * backend has 0 rows — Fix B for the P0 of 25-Sep-2026, when
 * `push_subscriptions` was found empty in production).
 */
export const getLocalPushStatus = async (opts = {}) => {
    if (!pushSupported()) {
        return { supported: false, permission: "unsupported", subscribed: false };
    }
    const permission = Notification.permission;
    const reg = await ensureServiceWorker();
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    const base = {
        supported: true,
        permission,        // "default" | "granted" | "denied"
        subscribed: !!sub,
        endpoint: sub ? sub.endpoint : null,
    };
    if (!opts.withBackendStatus) return base;
    try {
        const { data } = await api.get("/push/status");
        return {
            ...base,
            backendSubscriptions: typeof data?.subscriptions === "number" ? data.subscriptions : null,
        };
    } catch (_e) {
        return { ...base, backendSubscriptions: null };
    }
};

/**
 * Fix B (25-Sep-2026) — auto-reconcile a browser-local Subscription with the
 * backend when the two are out of sync (`permission=granted` + local sub +
 * `backendSubscriptions==0`). Reuses the existing endpoint / VAPID key, so
 * it does NOT need a user gesture. Safe to call at boot; no-op when already
 * in sync or when reconciliation is not possible.
 *
 * Returns `{ reconciled: bool, reason?: string }`.
 */
export const reconcileBackendSubscription = async () => {
    if (!pushSupported()) return { reconciled: false, reason: "unsupported" };
    if (Notification.permission !== "granted") return { reconciled: false, reason: "no-permission" };
    const reg = await ensureServiceWorker();
    if (!reg) return { reconciled: false, reason: "no-sw" };
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return { reconciled: false, reason: "no-local-sub" };
    // Ask backend how many subs it has for this user.
    let backendSubs = null;
    try {
        const { data } = await api.get("/push/status");
        backendSubs = data?.subscriptions ?? null;
    } catch (_e) {
        // /push/status requires auth; a 401 means no user is logged in yet.
        return { reconciled: false, reason: "not-authenticated" };
    }
    if (backendSubs && backendSubs > 0) {
        // Re-post anyway so `last_used_at` gets bumped — cheap and idempotent.
    }
    const json = sub.toJSON();
    try {
        await api.post("/push/subscribe", {
            endpoint: json.endpoint,
            keys: json.keys,
            expirationTime: json.expirationTime || null,
        });
        return { reconciled: true, backendSubsBefore: backendSubs };
    } catch (e) {
        return { reconciled: false, reason: `backend-post-failed: ${e?.message || e}` };
    }
};

/**
 * Full subscribe flow, MUST be called from a user-gesture handler
 * (button onClick) so Safari/Chrome accept the Notification.requestPermission
 * modal without silently rejecting.
 *
 * Returns the final status object. Throws only for actionable errors — the
 * caller shows a toast with the message.
 */
export const subscribeToPush = async () => {
    if (!pushSupported()) throw new Error("Este navegador no soporta notificaciones push.");

    // 1. Permission dance. Chrome + Firefox honour requestPermission()
    //    called from a gesture; Safari 16.4+ needs the same.
    let permission = Notification.permission;
    if (permission === "default") {
        permission = await Notification.requestPermission();
    }
    if (permission !== "granted") {
        throw new Error(
            permission === "denied"
                ? "El navegador está bloqueando notificaciones. Habilítalas desde el candado de la URL."
                : "No se otorgó permiso para notificaciones.",
        );
    }

    // 2. Ensure the SW is registered and ready.
    const reg = await ensureServiceWorker();
    if (!reg) throw new Error("No se pudo registrar el service worker.");

    // 3. Subscribe with our VAPID public key. If there's already a subscription
    //    from a previous session we reuse it (browser guarantees the same
    //    endpoint) — no double-subscribe.
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
        const vapidKey = await fetchVapidKey();
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
    }

    // 4. Send the subscription to the backend so future messages reach us.
    const json = sub.toJSON();
    await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys,
        expirationTime: json.expirationTime || null,
    });

    return { supported: true, permission: "granted", subscribed: true, endpoint: json.endpoint };
};

/**
 * Reverse of subscribeToPush — drop the browser subscription AND tell the
 * backend so no more messages are queued for the (now dead) endpoint.
 */
export const unsubscribeFromPush = async () => {
    if (!pushSupported()) return;
    const reg = await ensureServiceWorker();
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    const endpoint = sub.endpoint;
    try { await sub.unsubscribe(); } catch (_e) { /* ignore — best effort */ }
    try { await api.post("/push/unsubscribe", { endpoint }); }
    catch (_e) { /* backend may 404 on stale endpoint — ignore */ }
};

// ---------- SW → app postMessage bridge (for notification click nav) ----------

/**
 * Install a one-shot listener for the `notification-navigate` postMessage
 * fired by service-worker.js when the user clicks a notification. The
 * caller (App.js) uses this to route to the target conversation via
 * `history.pushState` — no full reload.
 *
 * `handler(url)` receives the target path (e.g. "/inbox?conversation_id=abc").
 */
let _navHandlerInstalled = false;
export const installNotificationNavHandler = (handler) => {
    if (_navHandlerInstalled) return;
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    _navHandlerInstalled = true;
    navigator.serviceWorker.addEventListener("message", (evt) => {
        if (evt?.data?.type === "notification-navigate" && evt.data.url) {
            try { handler(evt.data.url); }
            catch (e) { console.warn("[push] navigation handler failed:", e); }
        }
    });
};
